/**
 * Observatory downloads (decision 227; Diurnal inventory 01 §3.6 to §3.8) and the station registry and map
 * (sign-off 01-039): the sources Field offers, their stations and the three nearest to the survey. Decision 340: no
 * credits are shown; the data are staff-only (`data-use.ts`, docs/specs/basemag-import.md §2.3.1). The requests themselves are built in
 * crates/teslyn-core/src/basemag/observatory.rs, as the Diurnal tool builds them, and sent from Rust with no proxy.
 *
 * The registry (`observatories.json`, rebuilt by tools/dev/observatory-registry.mjs) keeps the tool's names,
 * regions and flags; positions and operators come from INTERMAGNET's observatory metadata, closed observatories
 * from the BGS GIN's own list, and BoM positions from the header of each genuine BoM file.
 */
import registry from './observatories.json';
import type { LatLon } from './naips-briefing.js';
import { haversineKm } from './planning.js';

/** `Source` in observatory.rs. */
export type ObservatorySource = 'bom_raw' | 'bgs_minute' | 'bgs_second' | 'usgs_second';

/**
 * Whose terms apply to an INTERMAGNET observatory's data (spec §2.3.1): Geoscience Australia's CC BY 4.0, the
 * USGS public domain, or INTERMAGNET's default CC BY-NC 4.0.
 */
export type ObservatoryLicence = 'ga_cc_by' | 'usgs_public_domain' | 'intermagnet_cc_by_nc';

export interface ObservatoryStation {
  readonly code: string;
  readonly name: string;
  readonly country?: string;
  readonly lat?: number;
  readonly lon?: number;
  readonly region?: string;
  /** Run by Geoscience Australia (the tool's GA menu). */
  readonly australian?: boolean;
  /** No longer recording (Gnangara). */
  readonly historical?: boolean;
  /** Listed by the BGS GIN as closed. */
  readonly closed?: boolean;
  /** The institute that runs it, from INTERMAGNET's metadata (absent when the metadata names none). */
  readonly institute?: string;
  readonly licence?: ObservatoryLicence;
}

export interface BomStation {
  readonly code: string;
  readonly name: string;
  readonly lat: number;
  readonly lon: number;
}

export const OBSERVATORY_REGIONS: readonly string[] = registry.regions;
export const INTERMAGNET_STATIONS: readonly ObservatoryStation[] =
  registry.stations as readonly ObservatoryStation[];
export const BOM_STATIONS: readonly BomStation[] = registry.bom;
const USGS = new Set(registry.usgs);

export interface ObservatorySourceInfo {
  readonly source: ObservatorySource;
  readonly label: string;
  /** What the person gets, in plain words. */
  readonly note: string;
}

export const OBSERVATORY_SOURCES: readonly ObservatorySourceInfo[] = [
  {
    source: 'bom_raw',
    label: 'BoM raw (5 Hz, Canberra 1 Hz)',
    note: 'Variations only, out the same day; Teslyn warns that their total does not follow the field.',
  },
  {
    source: 'bgs_minute',
    label: 'BGS INTERMAGNET 1-minute',
    note: 'The observatory total field, one value a minute.',
  },
  {
    source: 'bgs_second',
    label: 'BGS INTERMAGNET 1-second',
    note: 'The observatory total field each second; asked only up to 24 h 5 min ago.',
  },
  {
    source: 'usgs_second',
    label: 'USGS 1-second',
    note: 'USGS observatories only (the Americas and the Pacific).',
  },
];

const live = (s: ObservatoryStation): boolean => !s.historical && !s.closed;

/** The stations a source offers, Australian ones first for BGS; historical and closed ones left out. */
export function observatoryStations(source: ObservatorySource): readonly ObservatoryStation[] {
  if (source === 'bom_raw') return BOM_STATIONS;
  if (source === 'usgs_second') {
    return registry.usgs.map(
      (code) => INTERMAGNET_STATIONS.find((s) => s.code === code) ?? { code, name: code },
    );
  }
  const open = INTERMAGNET_STATIONS.filter(live);
  return [...open.filter((s) => s.australian), ...open.filter((s) => !s.australian)];
}

/** A source's stations by the tool's regions, in its region order (BoM: one group). */
export function stationsByRegion(
  source: ObservatorySource,
): readonly { readonly region: string; readonly stations: readonly ObservatoryStation[] }[] {
  const all = observatoryStations(source);
  if (source === 'bom_raw') return [{ region: 'BoM Space Weather Services', stations: all }];
  return OBSERVATORY_REGIONS.map((region) => ({
    region,
    stations: all.filter((s) => s.region === region),
  })).filter((g) => g.stations.length > 0);
}

/** The sources a station can be downloaded from: BoM codes are lower case, IAGA codes upper case. */
export function stationSources(code: string): readonly ObservatorySource[] {
  if (BOM_STATIONS.some((s) => s.code === code)) return ['bom_raw'];
  const s = INTERMAGNET_STATIONS.find((x) => x.code === code);
  if (!s || !live(s)) return [];
  return USGS.has(code) ? ['bgs_minute', 'bgs_second', 'usgs_second'] : ['bgs_minute', 'bgs_second'];
}

/**
 * The `n` nearest live INTERMAGNET observatories to a point, by great-circle distance (the tool's three nearest,
 * data.js:6224-6236).
 */
export function nearestObservatories(
  at: LatLon,
  n = 3,
): readonly { readonly station: ObservatoryStation; readonly km: number }[] {
  return INTERMAGNET_STATIONS.filter((s) => live(s) && s.lat !== undefined && s.lon !== undefined)
    .map((station) => ({
      station,
      km: haversineKm({ lat: at.lat, lng: at.lon }, { lat: station.lat!, lng: station.lon! }),
    }))
    .sort((a, b) => a.km - b.km)
    .slice(0, n);
}

/** A station on the observatory map. */
export interface MapStation {
  /** Unique on the map (`bom:lmd`, `imag:LRM`). */
  readonly key: string;
  readonly code: string;
  readonly name: string;
  readonly lat: number;
  readonly lon: number;
  readonly kind: 'intermagnet' | 'bom';
  readonly region: string;
  readonly australian: boolean;
  readonly sources: readonly ObservatorySource[];
}

/** Every station the map shows: BoM's four raw-file stations and the live INTERMAGNET observatories. */
export function observatoryMapStations(): readonly MapStation[] {
  const bom: MapStation[] = BOM_STATIONS.map((s) => ({
    key: `bom:${s.code}`,
    code: s.code,
    name: s.name,
    lat: s.lat,
    lon: s.lon,
    kind: 'bom',
    region: 'Australia & Pacific',
    australian: true,
    sources: ['bom_raw'],
  }));
  const imag: MapStation[] = INTERMAGNET_STATIONS.filter(
    (s) => live(s) && s.lat !== undefined && s.lon !== undefined,
  ).map((s) => ({
    key: `imag:${s.code}`,
    code: s.code,
    name: s.name,
    lat: s.lat!,
    lon: s.lon!,
    kind: 'intermagnet',
    region: s.region ?? '',
    australian: s.australian === true,
    sources: stationSources(s.code),
  }));
  return [...bom, ...imag];
}

/** Where a stored observatory series came from, by its format and the name Teslyn gave the file. */
export function seriesSource(series: {
  readonly source_format: string;
  readonly file_name: string;
}): ObservatorySource | null {
  switch (series.source_format) {
    case 'observatory_bom':
      return 'bom_raw';
    case 'observatory_hapi':
      return 'bgs_second';
    case 'observatory_iaga':
      // USGS files are saved as `<iaga><yyyymmdd>vsec.sec`; every other IAGA-2002 file is INTERMAGNET data
      return /vsec\.sec$/i.test(series.file_name) ? 'usgs_second' : 'bgs_minute';
    default:
      return null;
  }
}

/** The UTC day before `nowMs`, `YYYY-MM-DD` (the download form starts on it). */
export function yesterdayUtc(nowMs: number): string {
  return new Date(nowMs - 86_400_000).toISOString().slice(0, 10);
}
